import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, PhoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { extractErrorMessage, conversationAPI } from '../../services/api';
import { campaignsAPI } from '../../services/campaignsAPI';
import { userAssistantConfigService } from '../../services/userAssistantConfigService';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
/**
 * Minimal target shape — covers both leads and contacts. Anything with an
 * id, display name, and a phone number can feed this modal; `email` is
 * used only to forward to the run's outbound dispatch.
 */
export type CallTarget = {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
};

type CallLeadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lead: CallTarget | null;
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

const CallLeadModal: React.FC<CallLeadModalProps> = ({
  isOpen,
  onClose,
  lead,
  setToast,
}) => {
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [starting, setStarting] = useState(false);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const campaignDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!campaignDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target as Node)) {
        setCampaignDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [campaignDropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedCampaignId('');
    setLoadingCampaigns(true);
    // Only active campaigns are callable (LT-459) — a draft / paused
    // campaign has no live audience or version pin to dial against. The
    // backend re-checks status; this filter just keeps the picker honest.
    campaignsAPI
      .list()
      .then((list) => {
        setCampaigns(
          list
            .filter((c) => c.status === 'active')
            .map((c) => ({ id: c.id, name: c.name })),
        );
      })
      .catch(() => setCampaigns([]))
      .finally(() => setLoadingCampaigns(false));
  }, [isOpen]);

  // The Contacts "Call lead" flow dispatches a single ad-hoc call against
  // an active Campaign (LT-459) via POST /conversation/test-prompt with
  // campaign_id. The backend resolves the L1 agent from the campaign's
  // wrapped sequence and composes the full L1-L5 prompt — the campaign
  // supplies the L2 brief — the same prompt the sequence
  // VoiceCallStepExecutor builds (LT-448). Voice Gateway then dials the
  // contact's phone. The contact is not enrolled in the sequence.
  const handleCall = async () => {
    if (!lead || !selectedCampaignId) return;
    setStarting(true);
    try {
      const config = await userAssistantConfigService.getConfig().catch(() => null);
      const hasOutboundConfigured = (config?.outbound_assistant_id || '').trim();
      if (!hasOutboundConfigured) {
        const validation = await userAssistantConfigService.validateConfig().catch(() => null);
        const outboundMissing = !validation || validation.missing?.includes('outbound_assistant_id');
        if (outboundMissing) {
          setToast({
            type: 'error',
            message: 'Outbound assistant is not configured. Please set it up in Settings.',
          });
          setStarting(false);
          return;
        }
      }

      const response = await conversationAPI.testPrompt({
        campaign_id: selectedCampaignId,
        phone: lead.phone,
        // Link to the contact so the gateway webhook can write the
        // CallSummary + transcript + Activity against it on completion.
        contact_id: lead.id,
      });

      if (!response?.success) {
        throw new Error(response?.error || response?.message || 'Failed to dispatch call');
      }

      const campaignName = campaigns.find((c) => c.id === selectedCampaignId)?.name;
      track(AnalyticsEvents.CAMPAIGN_CREATED, {
        lead_count: 1,
        run_id: response.call_id || 'test-prompt',
        campaign_id: selectedCampaignId,
      });
      onClose();
      setToast({
        type: 'success',
        message:
          `Calling ${lead.name} at ${lead.phone}` +
          (campaignName ? ` for campaign "${campaignName}"` : '') +
          `. The call will dispatch in a few seconds.`,
      });
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Failed to start call');
      setToast({ type: 'error', message });
    } finally {
      setStarting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-6 h-6 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">Call lead</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          {lead && (
            <p className="text-sm text-gray-300">
              Call <span className="font-medium text-white">{lead.name}</span>
              {lead.phone && <span className="text-gray-400"> ({lead.phone})</span>}
            </p>
          )}
          <div ref={campaignDropdownRef} className="relative">
            <label
              id="call-lead-campaign-label"
              className="block text-sm font-medium text-gray-300 mb-1.5"
            >
              Select campaign *
            </label>
            {loadingCampaigns ? (
              <p className="text-gray-400 text-sm">Loading active campaigns...</p>
            ) : campaigns.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No active campaigns found. Launch a campaign first, then try again.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  id="call-lead-campaign"
                  aria-haspopup="listbox"
                  aria-expanded={campaignDropdownOpen}
                  aria-labelledby="call-lead-campaign-label"
                  onClick={() => setCampaignDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedCampaignId
                      ? campaigns.find((c) => c.id === selectedCampaignId)?.name ?? 'Choose a campaign'
                      : 'Choose a campaign'}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${campaignDropdownOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {campaignDropdownOpen && (
                  <ul
                    role="listbox"
                    aria-labelledby="call-lead-campaign-label"
                    className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar"
                  >
                    <li
                      role="option"
                      aria-selected={!selectedCampaignId}
                      onClick={() => {
                        setSelectedCampaignId('');
                        setCampaignDropdownOpen(false);
                      }}
                      className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedCampaignId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                    >
                      Choose a campaign
                    </li>
                    {campaigns.map((campaign) => (
                      <li
                        key={campaign.id}
                        role="option"
                        aria-selected={selectedCampaignId === campaign.id}
                        onClick={() => {
                          setSelectedCampaignId(campaign.id);
                          setCampaignDropdownOpen(false);
                        }}
                        className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedCampaignId === campaign.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                      >
                        {campaign.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCall}
              disabled={
                starting || loadingCampaigns || !selectedCampaignId || campaigns.length === 0 || !lead
              }
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {starting ? 'Starting…' : 'Start call'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallLeadModal;
